import {
  AfterViewInit,
  Component,
  DoCheck, effect,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {inOutAnimation} from 'src/app/shared/animations/animations';
import {SecurityService} from '../../../security/security.service';
import {ConfigurationService} from '../../../configuration/configuration.service';
import {ConfigurationModel, FeatureFlagEnum} from '../../../shared/models/configuration.model';
import {Subscription} from 'rxjs';
import {environment} from '../../../../environments/environment';
import {SettingsGlobalSearchService} from './settings-global-search.service';
import {RootRoute, SettingsRoute} from '../../../shared/models/angular-routing';
import {NgClass, NgOptimizedImage} from '@angular/common';
import {MatTooltip} from '@angular/material/tooltip';
import {RouterLink, RouterLinkActive, RouterOutlet} from '@angular/router';
import {MatIconButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {MatProgressBar} from '@angular/material/progress-bar';
import {MatRipple} from '@angular/material/core';
import {rxResource} from '@angular/core/rxjs-interop';

interface SettingSection {
  id: string;
  icon: string;
  name: string;
  description: string;
}

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  animations: [inOutAnimation],
  imports: [
    NgClass,
    MatTooltip,
    RouterLink,
    MatIconButton,
    MatIcon,
    MatRipple,
    RouterLinkActive,
    MatProgressBar,
    RouterOutlet,
    NgOptimizedImage,
  ],
})
export class SettingsComponent implements OnInit, OnDestroy, DoCheck, AfterViewInit {

  private securityService = inject(SecurityService);
  private configurationService = inject(ConfigurationService);
  // todo: the search seems not to be used, or is it planned in future?
  private searchService = inject(SettingsGlobalSearchService);

  public settingsSections: SettingSection[] = [];

  public dashboardVersion = environment.version;
  public envName = environment.name;
  public readonly RootRoute = RootRoute;

  private readonly openSubscriptions = Array<Subscription>();

  /**
   * An invisible `<div>` located at the very top of the main scrollable content. Used to detect whether
   * the main content is scrolled or not.
   * @private
   */
  @ViewChild('mainContentTopMark') private mainContentTopMark: ElementRef<HTMLElement>;
  private mainContentTopMarkObserver = new IntersectionObserver((entries, _) => {
    entries.forEach(entry => {
      const isTopMarkVisible = entry.intersectionRatio > 0;
      this.isMainContentScrolled = !isTopMarkVisible;
    });
  });
  isMainContentScrolled = false;

  /**
   * Used to avoid ExpressionChangedAfterItHasBeenCheckedError from happening when `<app-manage-users>`
   * is added and calls `SettingsComponent.registerListener()` during its `ngOnInit()`.
   * This field is used to be the source of truth for `showSearchField` but the `showSearchField` is only
   * allowed to refresh its value during the _safe_ `ngDoCheck()` callback.
   * @private
   */
  private showSearchFieldSafe = false;
  showSearchField = this.showSearchFieldSafe;
  private searchHintSafe: string;
  searchHint: string;

  /**
   * Used to remember the very last valid **normalized** search term.
   * @private
   */
  private lastNormalizedSearchTerm: string | null = null;
  searchTerm: string | null = this.lastNormalizedSearchTerm;

  configurationResource = rxResource({
    stream: () => this.configurationService.sharedConfigurationModel,
  });

  hasAdminRoleResource = rxResource({
    params: () => {
      const config = this.configurationResource.value();
      // Return whatever data is needed for the next call
      return {config};
    },
    stream: () => this.securityService.isAdmin$()
  });


  /**
   * The constructor.
   */
  constructor() {
    effect(() => {
      const config = this.configurationResource.value();
      if (config !== undefined) {
        this.handleConfigurationUpdate(this.configurationResource.value());
      }
    });

    effect(() => {
      const hasAdminRole = this.hasAdminRoleResource.value();
      if (hasAdminRole !== undefined) {
        this.handleConfigurationUpdate(this.configurationResource.value(), hasAdminRole);
      }
    });
  }

  ngDoCheck() {
    // Let's refresh some properties used in the views at this Angular lifecycle step.
    // It would be an ExpressionChangedAfterItHasBeenCheckedError if their value changed in some later lifecycle steps.
    this.searchHint = this.searchHintSafe;
    this.showSearchField = this.showSearchFieldSafe;
  }

  ngOnInit() {
    const topicsSubscription = this.searchService.topics.subscribe(topics => {
      this.onSubscribedTopicsChanged(topics);
    });
    this.openSubscriptions.push(topicsSubscription);
  }

  ngAfterViewInit() {
    this.attachTopMarkVisibilityObserver();
  }

  ngOnDestroy() {
    this.mainContentTopMarkObserver?.disconnect();

    this.openSubscriptions.forEach(subscription => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
  }

  onSearchTermChanged() {
    // todo: the search seems not to be used, or is it planned in future?
    let normalizedSearchTerm: string | null = this.searchTerm.trim();
    if (normalizedSearchTerm === '') {
      normalizedSearchTerm = null;
    }

    if (this.lastNormalizedSearchTerm !== normalizedSearchTerm) {
      this.lastNormalizedSearchTerm = normalizedSearchTerm;
      this.searchService.search(normalizedSearchTerm);
    }
  }

  private handleConfigurationUpdate(cfg: ConfigurationModel, isAdmin: boolean = false) {
    const menuSecurityConfig: MenuSecurityConfig = {
      cartegraphIntegrationEnabled: cfg?.hasFeatureFlag(FeatureFlagEnum.CartegraphIntegration),
      isAdmin,
      supervisorPermissions: cfg?.supervisorPermissions ?? [],
    };
    // Let's show a non-admin menu as soon as possible
    this.updateMenu(menuSecurityConfig);
  }

  private onSubscribedTopicsChanged(topics: string[]) {
    this.searchHintSafe = 'Search for ' + topics.join(', ');
    this.showSearchFieldSafe = topics.length > 0;
  }

  private updateMenu(config: MenuSecurityConfig) {
    this.settingsSections.length = 0;

    if (config.isAdmin || config.supervisorPermissions.find(p => p === 'users') !== undefined) {
      this.settingsSections.push({
        id: SettingsRoute.MANAGE_USERS,
        icon: 'people',
        name: 'Users',
        description: 'Manage users and assign roles',
      });
    }
    if (config.isAdmin || config.supervisorPermissions.find(p => p === 'vehicles') !== undefined) {
      this.settingsSections.push({
        id: SettingsRoute.MANAGE_VEHICLES,
        icon: 'front_loader',
        name: 'Vehicles',
        description: 'Manage and configure your fleet',
      });
    }
    if (config.isAdmin || config.supervisorPermissions.find(p => p === 'routes') !== undefined) {
      this.settingsSections.push({
        id: SettingsRoute.MANAGE_ROUTES,
        icon: 'route',
        name: 'Routes',
        description: 'Upload and manage your routes',
      });
    }
    if (config.isAdmin || config.supervisorPermissions.find(p => p === 'observations') !== undefined) {
      this.settingsSections.push({
        id: SettingsRoute.MANAGE_OBSERVATIONS,
        icon: 'pin_drop',
        name: 'Observations',
        description: 'Create and manage driver observations',
      });
    }
    if (config.isAdmin || config.supervisorPermissions.find(p => p === 'map_settings') !== undefined) {
      this.settingsSections.push({
        id: SettingsRoute.MAP_SETTINGS,
        icon: 'map',
        name: 'Map Settings',
        description: 'Edit default map settings',
      });
    }
    if (config.isAdmin || config.supervisorPermissions.find(p => p === 'public') !== undefined) {
      this.settingsSections.push({
        id: SettingsRoute.MANAGE_PUBLIC_PORTAL,
        icon: 'preview',
        name: 'Public Portal',
        description: 'Share your progress with the public',
      });
    }

    // vehicle installation report not used at this time
    /*if (config.isAdmin) {
      const addAtIndex = this.settingsSections.findIndex(x => x.id === SettingsRoute.MANAGE_VEHICLES) + 1;
      const vehicleInstallationSection: SettingSection = {
        id: SettingsRoute.VEHICLE_INSTALLATIONS,
        icon: 'no_crash',
        name: 'Vehicle Installations',
        description: 'See vehicle installations report',
      };
      this.settingsSections.splice(addAtIndex, 0, vehicleInstallationSection);
    }*/

    if (config.isAdmin || config.supervisorPermissions.find(p => p === 'forms') !== undefined) {
      this.settingsSections.push({
        id: SettingsRoute.INSPECTION_FORMS,
        icon: 'fact_check',
        name: 'Inspection Forms',
        description: 'Manage pre and post trip inspection forms',
      });
    }

    if (config.isAdmin && config.cartegraphIntegrationEnabled) {
      this.settingsSections.push({
        id: SettingsRoute.CARTEGRAPH,
        icon: 'done',
        name: 'Cartegraph',
        description: '',
      });
    }

    this.settingsSections.push({
      id: SettingsRoute.MY_ACCOUNT,
      icon: 'account_circle',
      name: 'Account',
      description: 'Manage your account',
    });
  }

  private attachTopMarkVisibilityObserver() {
    if (!IntersectionObserver) {
      console.warn('Intersection Observer API not supported');
      return;
    }

    this.mainContentTopMarkObserver.observe(this.mainContentTopMark.nativeElement);
  }
}

interface MenuSecurityConfig {
  cartegraphIntegrationEnabled: boolean;
  isAdmin: boolean;
  supervisorPermissions: string[];
}
